/**
 * Client for the processing worker: lazy singleton, promise-per-request, and
 * a transparent main-thread fallback (same handler module) when Worker is
 * unavailable (tests, legacy embeds) or the worker script fails to load.
 *
 * Inputs are deliberately NOT transferred, only cloned — a request must stay
 * replayable on the main thread if the worker dies mid-flight. Results may
 * arrive transferred (ArrayBuffers).
 */

let worker = null;
let workerUnavailable = false;
let nextRequestId = 0;
const pending = new Map();

function failOverToMainThread() {
  workerUnavailable = true;
  if (worker) {
    worker.terminate();
    worker = null;
  }
  const entries = Array.from(pending.values());
  pending.clear();
  entries.forEach(entry => {
    runOnMainThread(entry.type, entry.payload).then(entry.resolve, entry.reject);
  });
}

function getWorker() {
  if (worker || workerUnavailable) return worker;
  if (typeof Worker === 'undefined') {
    workerUnavailable = true;
    return null;
  }
  try {
    worker = new Worker(new URL('./processing.worker.js', import.meta.url), { type: 'module' });
    worker.onmessage = (event) => {
      const { id, kind, payload } = event.data;
      const entry = pending.get(id);
      if (!entry) return;
      if (kind === 'result') {
        pending.delete(id);
        entry.resolve(payload);
      } else if (kind === 'error') {
        pending.delete(id);
        entry.reject(new Error(payload.message));
      } else if (kind === 'progress' && entry.onProgress) {
        entry.onProgress(payload);
      }
    };
    // Load/runtime failure of the worker itself (not a handler error):
    // permanently fall back and replay in-flight requests on the main thread.
    worker.onerror = () => failOverToMainThread();
  } catch {
    workerUnavailable = true;
    worker = null;
  }
  return worker;
}

async function runOnMainThread(type, payload) {
  const handlers = await import('./processingHandlers');
  switch (type) {
    case 'parseXlsx':
      return handlers.parseXlsxWorkbook(payload.data);
    case 'exportXlsx':
      return handlers.exportScheduleXlsx(payload);
    default:
      throw new Error(`Unknown processing task: ${type}`);
  }
}

/**
 * Run a processing task off the main thread when possible.
 * @param {'parseXlsx'|'exportXlsx'} type
 * @param {Object} payload - structured-cloneable (pass Pinia data through deepToRaw first)
 * @param {Object} [options]
 * @param {Function} [options.onProgress]
 */
export function runProcessingTask(type, payload, { onProgress } = {}) {
  const activeWorker = getWorker();
  if (!activeWorker) return runOnMainThread(type, payload);

  return new Promise((resolve, reject) => {
    const id = ++nextRequestId;
    pending.set(id, { type, payload, resolve, reject, onProgress });
    try {
      activeWorker.postMessage({ id, type, payload });
    } catch (error) {
      // DataCloneError etc. — this payload can't cross the boundary.
      pending.delete(id);
      reject(error);
    }
  });
}
